import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import './MyProfile.css';

import axios from 'axios';
import {
    buildSigninUrl,
    GUEST_CREATE_ACCOUNT_LABEL,
    GUEST_SIGN_IN_LABEL,
    isGuestPostsViewEnabled,
} from '../../support/openDiscussions';
import {
    clearUserData,
    getUserData,
    setUserData as cacheUserData,
} from '../../DataCache/userData';
import {
    clearProfileIdentity,
    getProfileIdentity,
    setProfileIdentity as cacheProfileIdentity,
} from '../../DataCache/profileIdentity';
import { FiSettings, FiUser, FiUsers } from 'react-icons/fi';
import {
  PROFILE_MODES,
  activeProfileView,
  buildActiveProfilePicturePreview,
  commitActiveProfileModeSwitch,
  resolveActiveProfileMode,
  setStoredActiveProfileMode,
} from '../../profileIdentity/activeProfileMode';
import { parseProfileHandleQuery } from '../../profileIdentity/profileHandleRouting';
import {
  PROFILE_UPDATED_EVENT,
  applyProfileAvatarImgFallback,
} from '../profilePictureUrl';
import { getUniqueDefaultAvatarDataUrl } from '../../../utils/uniqueDefaultAvatar';
import { appApiUrl, resolveAppApiOrigin } from '../../../utils/appApiOrigin';
import { performClientSignOut } from '../../../utils/sessionSignOut';

function isPostsGuestContext() {
    return (
        isGuestPostsViewEnabled() &&
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/posts')
    );
}

function applyUserProfile(profile, baseUrl, setUserData, setProfilePicPreview, profileIdentity = null) {
  if (!profile || typeof profile !== 'object') {
    setUserData(null);
    setProfilePicPreview(getUniqueDefaultAvatarDataUrl());
    return;
  }
  setUserData(profile);
  setProfilePicPreview(buildActiveProfilePicturePreview(profile, profileIdentity, baseUrl));
}

const MyProfile = ({
    guestBrowse = false,
    onManageProfileClick,
    userProfile: userProfileProp,
    accountOnly = false,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const baseUrl = resolveAppApiOrigin() || process.env.REACT_APP_BASE_URL || '';
    const initialProfile = userProfileProp ?? getUserData();
    const initialIdentity = getProfileIdentity();
    const [userData, setUserData] = useState(initialProfile);
    const [profileIdentity, setProfileIdentity] = useState(initialIdentity);
    const [switchingProfile, setSwitchingProfile] = useState(false);
    const [profileLoading, setProfileLoading] = useState(!initialProfile);
    const [profilePicPreview, setProfilePicPreview] = useState(
        buildActiveProfilePicturePreview(initialProfile, initialIdentity, baseUrl)
    );

    const loadUserProfile = useCallback(async () => {
        try {
            const response = await axios.post(appApiUrl('/api/profile'), {}, { withCredentials: true });
            if (response.data.success) {
                const profile = response.data.userProfile ?? null;
                const identity = response.data.profileIdentity || null;
                applyUserProfile(profile, baseUrl, setUserData, setProfilePicPreview, identity);
                cacheUserData(profile);
                cacheProfileIdentity(identity);
                setProfileIdentity(identity);
                if (response.data.profileIdentity?.activeProfileMode) {
                    setStoredActiveProfileMode(response.data.profileIdentity.activeProfileMode);
                }
            } else {
                setUserData(null);
                cacheUserData(null);
                cacheProfileIdentity(null);
                setProfileIdentity(null);
            }
        } catch (error) {
            console.error('User details not found ', error);
            setUserData(null);
            cacheUserData(null);
            cacheProfileIdentity(null);
            setProfileIdentity(null);
        } finally {
            setProfileLoading(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        const resolvedProfile = userProfileProp ?? getUserData();
        const cachedIdentity = getProfileIdentity();
        if (resolvedProfile) {
            applyUserProfile(resolvedProfile, baseUrl, setUserData, setProfilePicPreview, cachedIdentity);
            setProfileIdentity(cachedIdentity);
            setProfileLoading(false);
        }

        loadUserProfile();
    }, [baseUrl, userProfileProp, loadUserProfile]);

    useEffect(() => {
        const onProfileUpdated = () => {
            loadUserProfile();
        };
        window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
        return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    }, [loadUserProfile]);

    useEffect(() => {
        const onSessionEnded = () => {
            setUserData(null);
            setProfileIdentity(null);
            cacheUserData(null);
            cacheProfileIdentity(null);
            setProfilePicPreview(getUniqueDefaultAvatarDataUrl());
        };
        window.addEventListener('bahlus-session-ended', onSessionEnded);
        return () => window.removeEventListener('bahlus-session-ended', onSessionEnded);
    }, []);
    
    const MessageWithLabel = ({ message }) => {
        // Function to limit message length and add ellipsis
        const truncateMessage = (msg, maxLength) => {
          return msg.length > maxLength ? msg.substring(0, maxLength) + "..." : msg;
        };
      
        return (
          <span className="msg-labels">
            {truncateMessage(message, 30)}
          </span>
        );
    };
    
    const handleSignout = async () => {
        try {
            await performClientSignOut();
            navigate('/signin');
        } catch (error) {
            console.error('Error during logout:', error);
            navigate('/signin');
        }
    };

    const handleManageActivity = () => {
        onManageProfileClick?.();
        if (accountOnly) {
            navigate('/d/profile');
            return;
        }
        if (location.pathname.startsWith('/posts')) {
            navigate('/posts?view=manage-profile');
            return;
        }
        navigate('/support?view=manage-profile');
    };

    const switchProfileMode = async (mode) => {
        const next = mode === PROFILE_MODES.SOCIAL ? PROFILE_MODES.SOCIAL : PROFILE_MODES.PERSONAL;
        if (next === PROFILE_MODES.SOCIAL && !profileIdentity?.social?.enabled) return;
        setSwitchingProfile(true);
        try {
            const response = await axios.post(
                `${baseUrl}/api/profile-identity/active-mode`,
                { profileMode: next },
                { withCredentials: true }
            );
            if (response.data?.success) {
                const nextUser = response.data.userProfile;
                const nextIdentity = response.data.profileIdentity;
                cacheUserData(nextUser);
                cacheProfileIdentity(nextIdentity);
                setProfileIdentity(nextIdentity);
                commitActiveProfileModeSwitch(nextIdentity?.activeProfileMode || next);
                applyUserProfile(nextUser, baseUrl, setUserData, setProfilePicPreview, nextIdentity);
            }
        } catch (error) {
            console.error('Failed to switch profile', error);
        } finally {
            setSwitchingProfile(false);
        }
    };
    
    const handlePublicProfileActivity = () => {
        onManageProfileClick?.();
        if (accountOnly) {
            navigate('/d/profile');
            return;
        }
        const userId = userData?.userId;
        if (!userId) {
            navigate('/posts');
            return;
        }
        const mode = resolveActiveProfileMode(userData, profileIdentity);
        const handleRaw =
            mode === PROFILE_MODES.SOCIAL && profileIdentity?.social?.enabled
                ? profileIdentity.social.handle
                : profileIdentity?.personal?.handle;
        const handle = parseProfileHandleQuery(handleRaw) || '';
        const params = new URLSearchParams({
            view: 'user-profile',
            userId: String(userId),
        });
        if (handle) params.set('handle', handle);
        if (mode === PROFILE_MODES.SOCIAL) params.set('profileMode', 'social');
        navigate({
            pathname: '/posts',
            search: `?${params.toString()}`,
        });
    };

    if (profileLoading) {
        return (
            <div className="profile-container">
                <div
                    className="profile-dropdown profile-dropdown-skeleton"
                    aria-busy="true"
                    aria-label="Loading profile"
                >
                    <ul className="user-header-list">
                        <li className="user-header">
                            <span className="usr-profile-pic" aria-hidden>
                                <span className="profile-skeleton-block profile-skeleton-avatar-fill" />
                            </span>
                            <div className="user-info">
                                <span className="manage-profile-icon" aria-hidden>
                                    <span className="profile-skeleton-block profile-skeleton-manage-icon-fill" />
                                </span>
                                <span className="usr-metadata-name">
                                    <span className="profile-skeleton-block profile-skeleton-name-fill" />
                                </span>
                            </div>
                        </li>
                    </ul>

                    <h3 className="profile-heading">
                        <span className="profile-skeleton-block profile-skeleton-heading-fill" />
                    </h3>
                    <ul>
                        {[1, 2, 3].map((i) => (
                            <li key={`acct-${i}`}>
                                <span className="usr-labels">
                                    <span className="profile-skeleton-block profile-skeleton-label-fill" />
                                </span>
                                <span className="usr-metadata">
                                    <span className="profile-skeleton-block profile-skeleton-value-fill" />
                                </span>
                            </li>
                        ))}
                    </ul>

                    <h3 className="profile-heading">
                        <span className="profile-skeleton-block profile-skeleton-heading-fill narrow" />
                    </h3>
                    <ul>
                        <li>
                            <span className="usr-labels">
                                <span className="profile-skeleton-block profile-skeleton-label-fill" />
                            </span>
                            <span className="usr-metadata">
                                <span className="profile-skeleton-block profile-skeleton-value-fill" />
                            </span>
                        </li>
                    </ul>

                    <div className="signout profile-signout-skeleton" aria-hidden>
                        <span className="profile-skeleton-block profile-skeleton-signout-fill" />
                    </div>
                </div>
            </div>
        );
    }

    if (!userData || typeof userData !== 'object') {
        const showGuestMenu = guestBrowse || isPostsGuestContext();
        const returnTo = `${location.pathname}${location.search}`;
        if (showGuestMenu) {
            return (
                <div className="profile-container">
                    <div className="profile-dropdown od-guest-profile-dropdown">
                        <Link
                            to={buildSigninUrl({ mode: 'signup', returnTo })}
                            className="od-guest-shell-create-account-btn od-guest-profile-dropdown__cta"
                        >
                            {GUEST_CREATE_ACCOUNT_LABEL}
                        </Link>
                        <Link to={buildSigninUrl({ returnTo })} className="od-guest-profile-dropdown__signin">
                            {GUEST_SIGN_IN_LABEL}
                        </Link>
                    </div>
                </div>
            );
        }
        return (
            <div className="profile-container">
                <div className="profile-dropdown">
                    <p className="profile-loading-text">Unable to load profile.</p>
                    <div className="signout" onClick={handleSignout}>Sign out</div>
                </div>
            </div>
        );
    }

    const displayName = [userData.first_name, userData.last_name].filter(Boolean).join(' ') || userData.email || '';
    const activeMode = resolveActiveProfileMode(userData, profileIdentity);
    const activeView = activeProfileView(userData, profileIdentity, activeMode);
    const socialEnabled = profileIdentity?.social?.enabled === true;

    return (
        <div className="profile-container">
            <div className="profile-dropdown">
            <ul className="user-header-list"> 
                <li className="user-header"> 
                    <span className="usr-profile-pic">
                            <img
                            src={profilePicPreview}
                            alt="Profile"
                            className="profile-pic-img"
                            onError={(e) => {
                              applyProfileAvatarImgFallback(e.target, {
                                userId: userData?.userId,
                                name: activeView.displayName || displayName,
                              });
                            }}
                            />
                    </span>

                    <div className="user-info">
                    <button
                      type="button"
                      className="manage-profile-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManageActivity();
                      }}
                      aria-label="Manage profile"
                      title="Manage"
                    >
                      <FiSettings aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="usr-metadata-name usr-metadata-name-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublicProfileActivity();
                      }}
                      aria-label={`View profile for ${activeView.displayName || displayName}`}
                    >
                        {activeView.displayName || displayName}
                    </button>
                    {activeView.handle ? (
                      <span className="usr-metadata-handle">{activeView.handle}</span>
                    ) : null}
                    </div> 

                </li>
            </ul>
            {socialEnabled && !accountOnly ? (
              <div className="profile-mode-switcher">
                <button
                  type="button"
                  className={`profile-mode-switcher-btn${activeMode === PROFILE_MODES.PERSONAL ? ' is-active' : ''}`}
                  disabled={switchingProfile}
                  onClick={() => switchProfileMode(PROFILE_MODES.PERSONAL)}
                >
                  <FiUser aria-hidden /> Personal
                </button>
                <button
                  type="button"
                  className={`profile-mode-switcher-btn${activeMode === PROFILE_MODES.SOCIAL ? ' is-active' : ''}`}
                  disabled={switchingProfile}
                  onClick={() => switchProfileMode(PROFILE_MODES.SOCIAL)}
                >
                  <FiUsers aria-hidden /> Social
                </button>
              </div>
            ) : null}

                <h3 className="profile-heading">Account</h3>
                <ul>
                    <li>
                        <span className="usr-labels"><MessageWithLabel message="User ID" /></span>
                        <span className="usr-metadata">{userData.userId ?? '—'}</span>
                    </li>
                    <li>
                        <span className="usr-labels"><MessageWithLabel message="Email" /></span>
                        <span className="usr-metadata">{userData.email ?? '—'}</span>
                    </li>
                    <li>
                        <span className="usr-labels"><MessageWithLabel message="Status" /></span>
                        <span className="usr-metadata">{userData.account_status ?? '—'}</span>
                    </li>
                </ul>

                <h3 className="profile-heading">Activity </h3>
                <ul>
                    <li>
                        <span className="usr-labels"><MessageWithLabel message="Last signin" /></span>
                        <span className="usr-metadata">{userData.lastLoginAt ?? '—'}</span>
                    </li>
                </ul>
            
                <div className="signout" onClick={handleSignout}>Sign out</div>
            </div>
        </div>
    );
};

export default MyProfile;